import { useState, useEffect } from 'react';
import { storyElement } from "@/lib/story-element";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowLeft, Gift } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { logActivity } from '@/lib/activity-logger';
import logoSvg from '@/assets/logo.svg';
import { useLocale } from '@/hooks/useLocale';
import { usePageMeta } from '@/hooks/usePageMeta';
import AppHeader from '@/components/layout/AppHeader';
import { isLiveHost } from '@/lib/isLiveHost';

const DEFAULT_AUTH_BG = '/images/auth-bg.png';

type AuthView = 'login' | 'signup' | 'forgot';

const Auth = () => {
  const { t } = useLocale();
  const liveHost = isLiveHost();

  usePageMeta({
    title: "Sign In or Join the Waitlist — Adventure Venture",
    description: "Sign in to Adventure Venture or join the closed-beta waitlist. We'll email you when your adventure begins.",
    noindex: true,
  });

  const [view, setView] = useState<AuthView>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // W1: require age + ToS acceptance before signup completes
  const [tosAccepted, setTosAccepted] = useState(false);
  // W6: start empty so the 2.7 MB background doesn't block the initial render;
  // useEffect sets it after mount so the auth form is interactive immediately.
  const [bgImage, setBgImage] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const refCode = searchParams.get('ref') || '';
  const modeParam = searchParams.get('mode');

  useEffect(() => {
    // W6: load after mount so the 2.7 MB default image doesn't block the auth form
    const stored = localStorage.getItem('auth_bg_image');
    setBgImage(stored || DEFAULT_AUTH_BG);
    // On live hosts, kick anyone trying to reach signup over to the waitlist.
    if (liveHost && (modeParam === 'signup' || refCode)) {
      navigate(`/waitlist?source=signup${refCode ? `&ref=${encodeURIComponent(refCode)}` : ''}`, { replace: true });
      return;
    }
    if (refCode) {
      setView('signup');
      localStorage.setItem('pending_referral_code', refCode);
    }
  }, [refCode, liveHost, modeParam, navigate]);

  // Central auth-state listener — handles both OAuth redirect and email/password sign-in.
  // Using a single navigate point here avoids the double-navigate that occurs when
  // handleLogin calls navigate('/') directly AND this subscription also fires SIGNED_IN.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        processStoredReferral();
        logActivity({ action: 'User signed in', category: 'auth', source: 'Auth' });
        navigate('/');
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  // OAuth top-level redirect completion: when the lovable broker redirects back
  // to /auth, tokens land in the URL fragment (#access_token=...&refresh_token=...)
  // or, on error, as #error=...&error_description=.... The @lovable.dev/cloud-auth-js
  // top-level flow does NOT parse this itself, so we do it here.
  useEffect(() => {
    const hash = window.location.hash?.startsWith('#') ? window.location.hash.slice(1) : '';
    const query = window.location.search?.startsWith('?') ? window.location.search.slice(1) : '';
    if (!hash && !query) return;
    const params = new URLSearchParams(hash || query);
    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const errParam = params.get('error') || params.get('error_description');

    if (errParam) {
      console.error('[google-oauth] callback error', errParam);
      toast.error(friendlyError(errParam));
      // Strip the hash/query so the toast doesn't re-fire on re-render
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (access_token && refresh_token) {
      (async () => {
        const { error } = await supabase.auth.setSession({ access_token, refresh_token });
        if (error) {
          console.error('[google-oauth] setSession failed', error);
          toast.error(friendlyError(error.message));
          return;
        }
        // Clear tokens from the URL bar; onAuthStateChange will navigate to '/'.
        window.history.replaceState({}, '', window.location.pathname);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/auth`,
      });
      if (result.error) {
        console.error("[google-oauth]", result.error);
        toast.error(friendlyError(result.error.message || ""));
        setIsLoading(false);
        return;
      }
      if (result.redirected) {
        // Browser is navigating to Google's consent screen; keep loading state.
        return;
      }
      // Tokens were returned directly; onAuthStateChange will navigate.
    } catch (err: any) {
      console.error("[google-oauth]", err);
      toast.error(friendlyError(err?.message || t.auth_err_generic));
      setIsLoading(false);
    }
  };

  const friendlyError = (msg: string): string => {
    const lower = msg.toLowerCase();
    if (lower.includes('invalid login credentials')) return t.auth_err_invalid_creds;
    if (lower.includes('email not confirmed')) return t.auth_err_email_not_confirmed;
    if (lower.includes('user already registered')) return t.auth_err_user_exists;
    if (lower.includes('password') && lower.includes('least')) return t.auth_err_password_too_short;
    if (lower.includes('rate limit') || lower.includes('too many')) return t.auth_err_rate_limit;
    if (lower.includes('signup is disabled')) return t.auth_err_signup_disabled;
    // Unknown error: return a generic message rather than leaking raw Supabase internals
    return t.auth_err_generic;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      // Navigation is handled by the onAuthStateChange subscription above to avoid
      // double-navigate (both here and from the subscription firing SIGNED_IN).
      toast.success(t.auth_welcome_back);
    } catch (error: any) {
      toast.error(friendlyError(error.message || t.auth_err_generic));
    } finally {
      setIsLoading(false);
    }
  };

  const processStoredReferral = async () => {
    const storedRef = localStorage.getItem('pending_referral_code');
    if (!storedRef) return;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await supabase.rpc('process_referral', {
        _referred_user_id: user.id,
        _referral_code: storedRef,
      });
      localStorage.removeItem('pending_referral_code');
    } catch (err) {
      console.error('Referral processing failed:', err);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error(t.auth_password_min);
      return;
    }
    setIsLoading(true);
    try {
      if (refCode) {
        localStorage.setItem('pending_referral_code', refCode);
      }
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: { full_name: fullName.trim() || undefined },
        },
      });
      if (error) throw error;
      toast.success(t.auth_check_email);
      logActivity({ action: 'User signed up', category: 'auth', source: 'Auth' });
      setView('login');
    } catch (error: any) {
      toast.error(friendlyError(error.message || t.auth_err_generic));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error(t.auth_enter_email);
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success(t.auth_reset_sent);
    } catch (error: any) {
      toast.error(friendlyError(error.message || t.auth_err_generic));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div {...storyElement("page-auth")} className="min-h-screen flex flex-col">
      <AppHeader />
      <div className="flex-1 flex items-center justify-center p-6 relative overflow-hidden">
      {/* W6: opacity transition — background fades in after mount so the auth form
           renders immediately without waiting for the 2.7 MB default image */}
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-700 ${bgImage ? "opacity-100" : "opacity-0"}`}
        style={bgImage ? { backgroundImage: `url(${bgImage})` } : undefined}
      />
      <div className="absolute inset-0 bg-black/40" />

      <Card className="w-full max-w-md shadow-2xl relative z-10 border-border/50 bg-card/90 backdrop-blur-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto">
            <img
              src={logoSvg}
              alt="Adventure Venture"
              className="w-20 h-[72px] drop-shadow-lg"
            />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            {view === 'forgot' ? t.auth_reset_password : 'Adventure Venture'}
          </CardTitle>
          <CardDescription className="text-muted-foreground/90">
            {view === 'login' && t.auth_sign_in_subtitle}
            {view === 'signup' && t.auth_sign_up_subtitle}
            {view === 'forgot' && t.auth_reset_subtitle}
          </CardDescription>
          {refCode && view === 'signup' && (
            <Badge variant="secondary" className="mx-auto gap-1.5">
              <Gift className="w-3 h-3" />
              {t.auth_referral_bonus}
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {/* ── LOGIN ── */}
          {view === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth_email}</Label>
                <Input id="email" type="email" placeholder={t.auth_email_placeholder} value={email}
                  onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t.auth_password}</Label>
                  <button type="button" onClick={() => setView('forgot')}
                    className="text-xs text-primary hover:underline" disabled={isLoading}>
                    {t.auth_forgot_password}
                  </button>
                </div>
                <Input id="password" type="password" placeholder={t.auth_password_placeholder} value={password}
                  onChange={(e) => setPassword(e.target.value)} required disabled={isLoading} minLength={6} />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.auth_signing_in}</> : t.auth_sign_in}
              </Button>
              <div className="flex items-center gap-3 my-1">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground uppercase">or</span>
                <Separator className="flex-1" />
              </div>
              <Button type="button" variant="outline" className="w-full gap-2" disabled={isLoading} onClick={handleGoogleSignIn}>
                <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>
              <p className="text-center text-sm">
                {liveHost ? (
                  <button
                    type="button"
                    onClick={() => navigate('/waitlist?source=signup')}
                    className="text-primary hover:underline"
                    disabled={isLoading}
                  >
                    Join the waitlist
                  </button>
                ) : (
                  <button type="button" onClick={() => setView('signup')} className="text-primary hover:underline" disabled={isLoading}>
                    {t.auth_no_account}
                  </button>
                )}
              </p>
            </form>
          )}

          {/* ── SIGNUP ── */}
          {view === 'signup' && !liveHost && (
            <form onSubmit={handleSignup} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="fullName">{t.auth_display_name}</Label>
                <Input id="fullName" type="text" placeholder={t.auth_name_placeholder} value={fullName}
                  onChange={(e) => setFullName(e.target.value)} disabled={isLoading} maxLength={80} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth_email}</Label>
                <Input id="email" type="email" placeholder={t.auth_email_placeholder} value={email}
                  onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">{t.auth_password}</Label>
                <Input id="password" type="password" placeholder={t.auth_password_signup_placeholder} value={password}
                  onChange={(e) => setPassword(e.target.value)} required disabled={isLoading} minLength={6} />
                <p className="text-xs text-muted-foreground">{t.auth_password_min}</p>
              </div>
              {/* W1: age-gate + ToS acceptance — required before account creation */}
              <div className="flex items-start gap-2.5 py-1">
                <Checkbox
                  id="tos-accept"
                  checked={tosAccepted}
                  onCheckedChange={(v) => setTosAccepted(v === true)}
                  disabled={isLoading}
                  className="mt-0.5 shrink-0"
                />
                <label htmlFor="tos-accept" className="text-xs text-muted-foreground leading-snug cursor-pointer">
                  I confirm I am 18 years of age or older and agree to the{" "}
                  <a href="/terms" className="underline text-foreground hover:text-primary" target="_blank" rel="noopener">
                    Terms of Service
                  </a>
                  .
                </label>
              </div>
              <Button type="submit" className="w-full" disabled={isLoading || !tosAccepted}>
                {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.auth_signing_up}</> : t.auth_sign_up}
              </Button>
              <div className="flex items-center gap-3 my-1">
                <Separator className="flex-1" />
                <span className="text-xs text-muted-foreground uppercase">or</span>
                <Separator className="flex-1" />
              </div>
              {/* W1: gate Google sign-up on ToS acceptance too */}
              <Button type="button" variant="outline" className="w-full gap-2" disabled={isLoading || !tosAccepted} onClick={handleGoogleSignIn}>
                <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                Continue with Google
              </Button>
              <p className="text-center text-sm">
                <button type="button" onClick={() => setView('login')} className="text-primary hover:underline" disabled={isLoading}>
                  {t.auth_has_account}
                </button>
              </p>
            </form>
          )}

          {/* ── FORGOT PASSWORD ── */}
          {view === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t.auth_email}</Label>
                <Input id="email" type="email" placeholder={t.auth_email_placeholder} value={email}
                  onChange={(e) => setEmail(e.target.value)} required disabled={isLoading} />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.auth_sending}</> : t.auth_send_reset_link}
              </Button>
              <p className="text-center text-sm">
                <button type="button" onClick={() => setView('login')} className="text-primary hover:underline inline-flex items-center gap-1" disabled={isLoading}>
                  <ArrowLeft className="w-3 h-3" /> {t.auth_back_to_sign_in}
                </button>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Auth;
